import ChartStacked from "@/components/charts/ChartStacked.vue";
import ChartYearly from "@/components/charts/ChartYearly.vue";
import Plotly from "@/components/charts/Plotly.vue";
import ELCCDashboard from "@/components/dashboards/ELCCDashboard.vue";
import LoadForecastDashboard from "@/components/dashboards/LoadForecastDashboard.vue";
import TStabilityDashboard from "@/components/dashboards/TStabilityDashboard.vue";
import DataTableYearly from "@/components/DataTableYearly.vue";
import OutputAADashboard from "@/components/OutputAADashboard.vue";
import OutputIRPDashboard from "@/components/OutputIRPDashboard.vue";
import OutputPCDashboard from "@/components/OutputPCDashboard.vue";
import OutputToolbar from "@/components/OutputToolbar.vue";
import ShortCircuitOutput from "@/components/ShortCircuitOutput.vue";
import PowerFlowDashboard from "@/components/dashboards/PowerFlowDashboard.vue";
import NPCOutputMixin from "@/mixins/outputs/npc";
import SSEOutputMixin from "@/mixins/outputs/sse";
import { DateTime } from "luxon";

const logo = require("@/assets/rex-logo.png");

export default {
  mixins: [SSEOutputMixin, NPCOutputMixin],
  data() {
    const url = `${window.location.protocol}//${window.location.host}${logo}`;
    return {
      caseName: this.currentCase ? this.currentCase.name : "Case",
      resourceItems: null,
      activeTabId: 0,
      PFpage: null,
      PFDates: Array(2).fill(
        this.currentCase
          ? this.currentCase.settings.POWER_FLOW.start_datetime.split("T")[0]
          : "2019-01-01"
      ),
      AAToolbarSettings: {
        viewMode: "dashboard",
      },
      PCToolbarSettings: {
        viewMode: "dashboard",
      },
      NodalPCToolbarSettings: {
        tab: "systemLevel",
        viewMode: "dashboard",
      },
      SSEDashboardToolbarSettings: {
        viewMode: "single",
      },
      PFElementMap: {
        bus: "nodes",
        line: "branches",
        trafo: "transformers2w",
        trafo3w: "transformers3w",
        storage: "storage",
        gen: "generatorsSync",
        sgen: "generatorsStatic",
        load: "demands",
        ext_grid: "markets",
        switch: "switches",
      },
      PFToolbarSettings: {
        pfTimeSeriesOutputType: "dashboard",
        year: null,
        outputSummary: true,
        viewMode: "violationReport",
        timeInterval: 60,
        hasTimeseries: true,
      },
      SSEToolbarSettings: {
        project: 0,
        year: null,
        storages: [],
        markets: [],
        generators: [],
        // renGens: [],
        // thermalGens: [],
        renewable_generators: [],
        thermal_generators: [],
        demands: [],
        outputSummary: true,
        viewMode: "annual",
        timeInterval: 60,
        hasTimeseries: false,
        hasDaily: false,
        selectedYear: [],
      },
      SSEDates: Array(2).fill(
        this.currentCase ? `${this.currentCase.settings.year_start}-01-01` : "2019-01-01"
      ),
      logoUrl: url,
      acelerexLogo: {
        source: url,
        xref: "paper",
        yref: "paper",
        x: 1,
        y: 0.05,
        sizex: 0.09,
        sizey: 0.09,
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.8,
        layer: "above",
      },
    };
  },
  computed: {
    capacityRequirement() {
      if (this.input == null) return null;
      return this.$utils
        .range(this.num_years)
        .map((idx) => this.input.demand_peak[idx] * (1 + this.input.capacity_margin[idx]));
    },
    inputCard() {
      return {
        title: "Inputs",
        children: [
          {
            title: "Demand (Input)",
            component: DataTableYearly,
            props: {
              itemKey: "name",
              items: [
                {
                  name: "Peak",
                  value: "demand_peak",
                },
                {
                  name: "Energy",
                  value: "demand_annual",
                },
              ].map(this.mapValue(this.input)),
            },
          },
          {
            title: "Capacity Requirement (Calculation)",
            component: DataTableYearly,
            props: {
              itemKey: "name",
              items: [
                {
                  name: "Installed Capacity Requirement",
                  value: this.capacityRequirement,
                },
              ],
            },
          },
        ],
      };
    },
    AAOutput() {
      const outputs = this.output.ALTERNATIVE_ANALYSIS;
      const mapValue = this.mapValue(outputs);
      const AADashboard = {
        component: OutputAADashboard,
        props: {
          caseOutputs: this.output,
          inputCase: this.currentCase,
          logoUrl: this.logoUrl,
        },
      };

      const page = {
        title: "Alternative Analysis",
        component: OutputToolbar,
        props: { settings: this.AAToolbarSettings },
        sections:
          this.AAToolbarSettings.viewMode === "dashboard"
            ? [AADashboard]
            : [
                this.inputCard,
                {
                  title: "Capacity",
                  children: [
                    {
                      title: "Sankey Chart",
                      component: ChartYearly,
                      props: {
                        chartComponent: Plotly,
                        playable: true,
                        yearStart: this.currentCase.settings.year_start,
                        data: Array(this.num_years)
                          .fill(1)
                          .map((_year, i) => [
                            {
                              type: "sankey",
                              orientation: "h",
                              node: {
                                pad: 15,
                                thickness: 30,
                                line: {
                                  color: "black",
                                  width: 0.5,
                                },
                                label: [
                                  ...outputs.fuel_types.map((type) =>
                                    this.snakeToTitleCase(type.type.toLowerCase())
                                  ),
                                  "Installed Capacity",
                                  "Peak Demand",
                                  "Excess",
                                ],
                              },

                              link: {
                                source: [
                                  ...outputs.fuel_types.map((_type, typeIndex) => typeIndex),
                                  outputs.fuel_types.length,
                                  outputs.fuel_types.length,
                                ],
                                target: [
                                  ...Array(outputs.fuel_types.length).fill(
                                    outputs.fuel_types.length
                                  ),
                                  outputs.fuel_types.length + 1, // Peak demand
                                  outputs.fuel_types.length + 2, // Excess
                                ],
                                value: [
                                  ...outputs.fuel_types.map((type) => type.result_capacity[i]),
                                  this.input.demand_peak[i], // Peak demand
                                  outputs.fuel_types.reduce(
                                    (res, type) => type.result_capacity[i] + res,
                                    0
                                  ) - this.input.demand_peak[i], // Excess
                                ],
                              },
                            },
                          ]),
                      },
                    },
                  ],
                },
                {
                  title: "Installed Capacity",
                  children: [
                    {
                      title: "Thermal Capacity (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "installed_capacity",
                        items: outputs.generators.filter((e) => e.type === "THERMAL"),
                      },
                      chart: {
                        component: ChartStacked,
                        props: {
                          type: "line",
                          valueKey: "installed_capacity",
                          items: outputs.generators.filter((e) => e.type === "THERMAL"),
                          yAxisLabel: "Capacity (MW)",
                          num_years: this.num_years,
                        },
                      },
                    },
                    {
                      title: "Renewable Capacity (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "installed_capacity",
                        items: outputs.generators.filter((e) => e.type === "RENEWABLE"),
                      },
                      chart: {
                        component: ChartStacked,
                        props: {
                          type: "line",
                          valueKey: "installed_capacity",
                          items: outputs.generators.filter((e) => e.type === "RENEWABLE"),
                          yAxisLabel: "Capacity (MW)",
                          num_years: this.num_years,
                        },
                      },
                    },
                    {
                      title: "Hydro Capacity (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "installed_capacity",
                        items: outputs.generators.filter((e) => e.type === "HYDRO"),
                      },
                      chart: {
                        component: ChartStacked,
                        props: {
                          type: "line",
                          valueKey: "installed_capacity",
                          items: outputs.generators.filter((e) => e.type === "HYDRO"),
                          yAxisLabel: "Capacity (MW)",
                          num_years: this.num_years,
                        },
                      },
                    },
                    {
                      title: "Storage Capacity (MW, MWh)",
                      children: [
                        {
                          component: DataTableYearly,
                          props: {
                            showHeader: true,
                            title: "Power Capacity (MW)",
                            headers: this.storageHeaders,
                            valueKey: "installed_capacity",
                            items: outputs.storage,
                            chart: {
                              component: ChartStacked,
                              props: {
                                type: "line",
                                valueKey: "installed_capacity",
                                items: outputs.storage,
                                yAxisLabel: "Capacity (MW)",
                                num_years: this.num_years,
                              },
                            },
                          },
                        },
                        {
                          component: DataTableYearly,
                          props: {
                            showHeader: true,
                            title: "Energy Capacity (MWh)",
                            headers: this.storageHeaders,
                            valueKey: "energy_capacity",
                            items: outputs.storage,
                          },
                        },
                      ],
                    },
                    {
                      title: "Demand Response Capacity (MW)",
                      component: DataTableYearly,
                      props: {
                        valueKey: "installed_capacity",
                        items: outputs.demand_response,
                      },
                      chart: {
                        component: ChartStacked,
                        props: {
                          type: "line",
                          valueKey: "installed_capacity",
                          items: outputs.demand_response,
                          yAxisLabel: "Capacity (MW)",
                          num_years: this.num_years,
                        },
                      },
                    },
                    {
                      title: "Energy Efficiency Capacity (MW)",
                      component: DataTableYearly,
                      props: {
                        valueKey: "installed_capacity",
                        items: outputs.energy_efficiency,
                      },
                      chart: {
                        component: ChartStacked,
                        props: {
                          type: "line",
                          valueKey: "installed_capacity",
                          items: outputs.energy_efficiency,
                          yAxisLabel: "Capacity (MW)",
                          num_years: this.num_years,
                        },
                      },
                    },

                    {
                      title: "Total",
                      component: DataTableYearly,
                      props: {
                        itemKey: "name",
                        items: [
                          {
                            name: "Firm Capacity (MW)",
                            value: "firm_capacity",
                          },
                          {
                            name: "Total Installed Capacity (MW)",
                            value: "installed_capacity",
                          },
                          {
                            name: "Capacity Margin (%)",
                            value: "capacity_margin",
                          },
                          {
                            name: "Objective Value ($)",
                            value: "objective_value",
                          },
                        ].map(mapValue),
                      },
                    },
                  ],
                },
                {
                  title: "Capacity by Fuel Type",
                  children: [
                    {
                      title: "Capacity by Fuel Type (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: [
                          {
                            text: "Type",
                            value: "type",
                          },
                          // { text: 'Source', value: 'source' },
                        ],
                        items: outputs.fuel_types,
                        itemKey: "type",
                        valueKey: "result_capacity",
                      },
                      chart: [
                        {
                          title: "Pie",
                          component: ChartYearly,
                          props: {
                            ...this.pieObjectByField(
                              "doughnut",
                              outputs.fuel_types,
                              "type",
                              "result_capacity"
                            ),
                          },
                        },
                        {
                          title: "Stacked Area",
                          component: ChartStacked,
                          props: {
                            type: "line",
                            nameKey: "type",
                            valueKey: "result_capacity",
                            items: outputs.fuel_types,
                            yAxisLabel: "Capacity (MW)",
                            num_years: this.num_years,
                          },
                        },
                      ],
                    },
                    {
                      title: "Energy by Fuel Type (MWh)",
                      component: DataTableYearly,
                      props: {
                        headers: [
                          {
                            text: "Type",
                            value: "type",
                          },
                          // { text: 'Source', value: 'source' },
                        ],
                        items: outputs.fuel_types,
                        itemKey: "type",
                        valueKey: "generation",
                      },
                      chart: [
                        {
                          title: "Pie",
                          component: ChartYearly,
                          props: {
                            ...this.pieObjectByField(
                              "doughnut",
                              outputs.fuel_types,
                              "type",
                              "generation"
                            ),
                          },
                        },
                        {
                          title: "Stacked Area",
                          component: ChartStacked,
                          props: {
                            type: "line",
                            nameKey: "type",
                            valueKey: "generation",
                            items: outputs.fuel_types,
                            yAxisLabel: "Capacity (MW)",
                            num_years: this.num_years,
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  title: "Planned Retirements and Builds",
                  children: [
                    {
                      title: "Deactivation (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "forced_deactivation",
                        items: outputs.generators,
                      },
                    },
                    {
                      title: "Planned Build (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "forced_build",
                        items: outputs.generators,
                      },
                    },
                  ],
                },
                {
                  title: "Economic Retirements and Builds",
                  children: [
                    {
                      title: "Economic Retirement (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "economic_retirement",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          // ...outputs.demand_response,
                          // ...outputs.energy_efficiency,
                        ],
                      },
                    },
                    {
                      title: "Economic Build (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "economic_build",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          // ...outputs.demand_response,
                          // ...outputs.energy_efficiency,
                        ],
                      },
                    },
                  ],
                },
                {
                  title: "Forced Deactivation and Builds",
                  children: [
                    {
                      title: "Forced Deactivation (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "forced_deactivation",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          // ...outputs.demand_response,
                          // ...outputs.energy_efficiency,
                        ],
                      },
                    },
                    {
                      title: "Forced Build (MW)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "forced_build",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          // ...outputs.demand_response,
                          // ...outputs.energy_efficiency,
                        ],
                      },
                    },
                  ],
                },
                {
                  title: "Operational Costs",
                  children: [
                    {
                      title: "Fuel Costs ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "fuel_cost",
                        items: outputs.generators,
                      },
                    },
                    {
                      title: "Emissions Costs ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "emissions_cost",
                        items: outputs.generators,
                      },
                    },
                    {
                      title: "VOM Cost ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "vom_cost",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          // ...outputs.demand_response,
                          // ...outputs.energy_efficiency,
                        ],
                      },
                    },
                    {
                      title: "FOM Cost ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "fom_cost",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          // ...outputs.demand_response,
                          // ...outputs.energy_efficiency,
                        ],
                      },
                    },
                    {
                      title: "Operational Cost ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "operational_cost",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          // ...outputs.demand_response,
                          // ...outputs.energy_efficiency,
                        ],
                      },
                    },
                    {
                      title: "Renewable Energy Curtailment (GWh)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "energy_curtailment",
                        items: outputs.generators.filter((e) => e.type === "RENEWABLE"),
                      },
                    },
                    {
                      title: "Lost Load (MWh)",
                      component: DataTableYearly,
                      props: {
                        items: [
                          {
                            value: Object.values(outputs.lost_load).map((y) =>
                              y.reduce((acc, i) => acc + i, 0)
                            ),
                          },
                        ],
                      },
                    },
                    {
                      title: "Totals",
                      component: DataTableYearly,
                      props: {
                        itemKey: "name",
                        items: [
                          {
                            name: "Zonal Price ($/MWh)",
                            value: "zonal_price",
                          },
                          {
                            name: "Capacity Price ($/kW-yr)",
                            value: "capacity_price",
                          },
                          {
                            name: "CO2 Emission (tons)",
                            value: "co2_emissions",
                          },
                          {
                            name: "Emissions Cost ($)",
                            value: "co2_emissions_cost",
                          },
                          {
                            name: "Generation Cost ($)",
                            value: "generation_cost",
                          },
                          {
                            name: "FOM Cost ($)",
                            value: "fom_cost",
                          },
                          {
                            name: "VOM Cost ($)",
                            value: "vom_cost",
                          },
                          {
                            name: "Fuel Cost ($)",
                            value: "fuel_cost",
                          },
                          {
                            name: "Renewable Energy Curtailment (TWh)",
                            value: "renewable_cutailment",
                          },
                        ].map(mapValue),
                      },
                    },
                    {
                      title: "Graph",
                      component: ChartStacked,
                      props: {
                        type: "bar",
                        items: [
                          {
                            name: "FOM Cost ($)",
                            value: "fom_cost",
                          },
                          {
                            name: "VOM Cost ($)",
                            value: "vom_cost",
                          },
                          {
                            name: "Emissions Cost ($)",
                            value: "co2_emissions_cost",
                          },
                          {
                            name: "Fuel Cost ($)",
                            value: "fuel_cost",
                          },
                        ].map(mapValue),
                        yAxisLabel: "Cost ($)",
                        num_years: this.num_years,
                      },
                    },
                  ],
                },
                {
                  title: "Capital Costs",
                  children: [
                    {
                      title: "Overnight Costs ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "overnight_costs",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          ...outputs.energy_efficiency,
                        ],
                      },
                    },
                    {
                      title: "Annuities ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "annuities",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          ...outputs.energy_efficiency,
                        ],
                      },
                    },
                    {
                      title: "Totals",
                      component: DataTableYearly,
                      props: {
                        itemKey: "name",
                        items: [
                          {
                            name: "Overnight Costs",
                            value: "total_overnight",
                          },
                          {
                            name: "Annuities",
                            value: "total_annuities",
                          },
                        ].map(mapValue),
                      },
                    },
                  ],
                },
              ],
      };
      return page;
    },
    PCOutput() {
      const outputs = this.output.PRODUCTION_COST;
      const mapValue = this.mapValue(outputs);

      const PCDashboard = {
        component: OutputPCDashboard,
        props: {
          caseOutputs: this.output,
          inputCase: this.currentCase,
          logoUrl: this.logoUrl,
        },
      };
      const mergeObjectField = (objects, field, headers = this.generatorHeaders) =>
        this.mergeObjectField(
          {
            inputSource: this.input,
            outputSource: outputs,
          },
          objects,
          field,
          headers
        );

      const energyByFuelType = Object.values(
        mergeObjectField("generators", "energy")
          .map((generator) => ({
            ...generator,
            value: Object.values(generator.value.profile)
              .map((id) => {
                const data = this.retrieveOutputProfile(id);
                if (data.length) {
                  return data;
                }
                return "[]";
              })
              .reduce(
                (arr, val, i) => ({
                  ...arr,
                  [i]: typeof val === "string" ? JSON.parse(val) : val,
                }),
                {}
              ),
          }))
          .reduce(this.groupBy("source"), {})
      );

      const page = {
        title: "Production Cost",
        component: OutputToolbar,
        props: { settings: this.PCToolbarSettings },
        sections:
          this.PCToolbarSettings.viewMode === "dashboard"
            ? [PCDashboard]
            : [
                this.inputCard,
                {
                  title: "Energy",
                  children: [
                    {
                      title: "Sankey Chart",
                      component: ChartYearly,
                      props: {
                        playable: true,
                        chartComponent: Plotly,
                        yearStart: this.currentCase.settings.year_start,
                        data: Array(this.num_years)
                          .fill(1)
                          .map((_year, yearIndex) => [
                            {
                              type: "sankey",
                              orientation: "h",
                              node: {
                                pad: 15,
                                thickness: 30,
                                line: {
                                  color: "black",
                                  width: 0.5,
                                },
                                label: [
                                  ...energyByFuelType.map((type) =>
                                    this.snakeToTitleCase(type.source.toLowerCase())
                                  ),
                                  "Generation",
                                  "Demand ",
                                ],
                              },
                              link: {
                                source: [
                                  ...energyByFuelType.map((_type, typeIndex) => typeIndex),
                                  energyByFuelType.length,
                                ],
                                target: [
                                  ...Array(energyByFuelType.length).fill(energyByFuelType.length), // Energy
                                  energyByFuelType.length + 1, // Annual demand
                                ],
                                value: [
                                  ...energyByFuelType.map((type) => type.value[yearIndex] / 1000),
                                  this.input.demand_annual[yearIndex], // Annual Demnad
                                ],
                              },
                            },
                          ]),
                      },
                    },
                  ],
                },
                {
                  title: "Metrics",
                  children: [
                    {
                      title: "Fuel Burn (MMBTU)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "fuel_burn",
                        items: outputs.generators,
                      },
                    },
                    {
                      title: "Fuel Cost ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "fuel_cost",
                        items: outputs.generators,
                      },
                    },
                    {
                      title: "VOM Cost ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "vom_cost",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          ...outputs.demand_response,
                        ],
                      },
                    },
                    {
                      title: "CO2 Emissions (Mtonne)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "co2_emissions",
                        items: outputs.generators,
                      },
                    },
                    {
                      title: "CO2 Emission Cost ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "co2_cost",
                        items: outputs.generators,
                      },
                    },
                    {
                      title: "Energy Sales ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "energy_revenue",
                        items: [...outputs.generators, ...outputs.storage],
                      },
                    },
                    {
                      title: "Renewable Energy Curtailment (GWh)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "energy_curtailment",
                        items: outputs.generators.filter((e) => e.type === "RENEWABLE"),
                      },
                    },
                    ...this.reserves.map((reserve) => ({
                      title: `${reserve.name} Reserve Sales ($)`,
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        items: mergeObjectField(
                          ["generators", "storage"],
                          "reserves",
                          this.generatorHeaders
                        ).map(this.mapObjectId(reserve.id, "revenue")),
                      },
                    })),
                    {
                      title: "FOM Cost ($)",
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        valueKey: "fom_cost",
                        items: [
                          ...outputs.generators,
                          ...outputs.storage,
                          ...outputs.demand_response,
                        ],
                      },
                    },
                    ...this.reserves.map((reserve) => ({
                      title: `${reserve.name} Reserve Cost ($)`,
                      component: DataTableYearly,
                      props: {
                        headers: this.generatorHeaders,
                        items: mergeObjectField(["generators", "storage"], "reserves").map(
                          this.mapObjectId(reserve.id, "cost")
                        ),
                      },
                    })),
                    // TODO: FIX
                    {
                      title: "Energy Price ($/MWh)",
                      component: Plotly,
                      props: {
                        toImageButtonOptions: this.chartDownloadName(
                          this.caseName,
                          "PC",
                          "Energy Price ($/MWh)"
                        ),
                        data: [
                          {
                            y: Object.values(outputs.zonal_price),
                            x: Object.keys(outputs.zonal_price),
                          },
                        ],
                      },
                    },
                    {
                      title: "Energy by Fuel Type (MWh)",
                      component: DataTableYearly,
                      props: {
                        headers: [
                          {
                            text: "Type",
                            value: "type",
                          },
                          {
                            text: "Source",
                            value: "source",
                          },
                        ],
                        items: energyByFuelType,
                      },
                    },
                    {
                      title: "Lost Load (MWh)",
                      component: DataTableYearly,
                      props: {
                        items: [{ value: Object.values(outputs.lost_load.profile) }],
                      },
                    },
                    {
                      title: "General",
                      component: DataTableYearly,
                      props: {
                        itemKey: "name",
                        items: [
                          {
                            name: "Zonal Price ($/MWh)",
                            value: "zonal_price",
                          },
                          {
                            name: "CO2 Emissions (tons)",
                            value: "co2_emissions",
                          },
                          {
                            name: "Emissions Cost ($)",
                            value: "co2_emissions_cost",
                          },
                          {
                            name: "Generation Cost ($)",
                            value: "generation_cost",
                          },
                          {
                            name: "VOM Cost ($)",
                            value: "vom_cost",
                          },
                          {
                            name: "FOM Cost ($)",
                            value: "fom_cost",
                          },
                          {
                            name: "Cost to Load ($)",
                            value: "load_cost",
                          },
                          {
                            name: "Total Fuel Cost ($)",
                            value: "fuel_cost",
                          },
                          {
                            name: "Objective Value ($)",
                            value: "objective_value",
                          },
                          {
                            name: "Renewable Energy Curtailment (TWh)",
                            value: "renewable_cutailment",
                          },
                        ].map(mapValue),
                      },
                    },
                  ],
                },
              ],
      };
      return page;
    },
    nodalPCYearlyOutputs() {
      return this.output.PRODUCTION_COST.yearlyResults;
    },
    defaultResources() {
      return this.resourceItems;
    },
    ELCCOutput() {
      const outputs = this.output.EFFECTIVE_LOAD_CARRYING_CAPABILITY;
      const page = {
        title: "ELCC",
        component: ELCCDashboard,
        props: {
          outputs,
          startYear: this.currentCase.settings.year_start,
        },
      };
      return page;
    },
    TStabilityOutput() {
      const outputs = this.output.TRANSIENT_STABILITY;
      const page = {
        title: "Transient Stability",
        component: TStabilityDashboard,
        props: {
          outputs,
          startYear: this.currentCase.settings.year_start,
        },
      };
      return page;
    },
    PowerFlowOutput() {
      const outputs = this.output.POWER_FLOW;
      const page = {
        title: "Power Flow",
        component: PowerFlowDashboard,
        props: {
          outputs,
          currentCase: this.currentCase,
        },
      };
      return page;
    },
    LoadForecastOutput() {
      const outputs = this.output.LOAD_FORECAST;
      const page = {
        title: "Load Forecast",
        component: LoadForecastDashboard,
        props: {
          outputs,
          startYear: this.currentCase.settings.year_start,
          inputCase: this.currentCase,
        },
      };
      return page;
    },
    PFDailyRecords() {
      return 1440 / this.currentCase.settings.POWER_FLOW.time_interval;
    },
    PFNumberOfDays() {
      return (
        DateTime.fromISO(this.PFEndDate, {
          zone: "utc",
        })
          .diff(
            DateTime.fromISO(this.PFStartDate, {
              zone: "utc",
            }),
            "days"
          )
          .toObject().days || 1
      );
    },
    SCOutput() {
      const page = {
        title: "Short Circuit",
        component: ShortCircuitOutput,
        props: { caseOutputs: this.output, caseSettings: this.currentCase.settings },
      };
      return page;
    },
    IRPDashboard() {
      const page = {
        title: "IRP Dashboard",
        component: OutputIRPDashboard,
        props: {
          caseOutputs: this.output,
          inputCase: this.currentCase,
          logoUrl: this.logoUrl,
          num_years: this.num_years,
        },
      };
      return page;
    },
  },
  watch: {
    activeTab(val) {
      this.activeTabId = val;
    },
    pages(val) {
      if (
        val &&
        val[this.activeTabId] &&
        val[this.activeTabId].title &&
        val[this.activeTabId].title === "Power Flow"
      ) {
        this.PFpage = true;
      } else {
        this.PFpage = false;
      }
    },
    PFsettings(val) {
      if (val) {
        this.PFToolbarSettings.timeInterval = val.time_interval;
        this.PFDates = Array(2).fill(DateTime.fromISO(val.start_datetime).toISODate());
      }
    },
  },
  methods: {
    fixNumber(number) {
      return number.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
    },
    getViolatedDates(elementName) {
      const allViolatedDates = Object.values(
        this.pfViolationOutput[elementName].map((elements) =>
          Object.values(elements).filter((elementType) => elementType.violation !== 0)
        )
      )
        .map((elements) => elements.map((elementType) => elementType.datetime))
        .flat();
      return allViolatedDates.filter((value, index) => allViolatedDates.indexOf(value) === index);
    },

    resourceInput(projectIndex, resourceType, resourceId) {
      return this.currentCase.input.projects[projectIndex][resourceType].find(
        (r) => r.id === resourceId
      );
    },

    datetimeBetweenDates(datetime) {
      return (
        DateTime.fromISO(datetime, {
          zone: "utc",
        }) >=
          DateTime.fromISO(this.PFpage ? this.PFStartDate : this.SSEStartDate, {
            zone: "utc",
          }) &&
        DateTime.fromISO(datetime, {
          zone: "utc",
        }) <
          DateTime.fromISO(this.PFpage ? this.PFEndDate : this.SSEEndDate, {
            zone: "utc",
          }).endOf("day")
      );
    },
    nodalPCTableData(resource, key) {
      return Object.values(this.nodalPCYearlyOutputs)[0][resource].map((el, i) => ({
        name: el.name,
        value: Object.values(this.nodalPCYearlyOutputs).map((year) => year[resource][i][key]),
      }));
    },
  },
};
